/**
 * App Component
 *
 * Componente root dell'applicazione.
 * Configura providers e routing.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryProvider } from '@/config/queryClient';
import { AuthProvider } from '@/features/auth/context/AuthContext';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { ScrollToTop } from '@/shared/components/ScrollToTop';
import Layout from '@/shared/components/Layout';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Viaggi = lazy(() => import('./pages/Viaggi'));
const ViaggioDetail = lazy(() => import('./pages/ViaggioDetail'));
const Questionari = lazy(() => import('./pages/Questionari'));
const Analytics = lazy(() => import('./pages/Analytics'));
const TripAnalyticsPage = lazy(() => import('./pages/TripAnalyticsPage'));
const DestinationAnalyticsPage = lazy(() => import('./pages/DestinationAnalyticsPage'));
const TripTypeAnalyticsPage = lazy(() => import('./pages/TripTypeAnalyticsPage'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Public survey pages (no auth required)
const QuestionarioPasseggeri = lazy(() => import('./pages/QuestionarioPasseggeri'));
const QuestionarioTourLeader = lazy(() => import('./pages/QuestionarioTourLeader'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function App() {
  return (
    <QueryProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Public survey routes (no auth) */}
                <Route path="/survey/p/:token" element={<QuestionarioPasseggeri />} />
                <Route path="/survey/tl/:token" element={<QuestionarioTourLeader />} />

                {/* Protected routes with layout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/viaggi" element={<Viaggi />} />
                  <Route path="/viaggi/:tripCode" element={<ViaggioDetail />} />
                  <Route path="/questionari" element={<Questionari />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/analytics/trips/:tripCode" element={<TripAnalyticsPage />} />
                  <Route path="/analytics/destinations/:destinationId" element={<DestinationAnalyticsPage />} />
                  <Route path="/analytics/types/:typeId" element={<TripTypeAnalyticsPage />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryProvider>
  );
}

export default App;
